/**
 * Traduz deeplink de notificação (API/legado) para rota SPA do novo layout.
 *
 * Exemplos:
 * - `/api/sale/553?from=notification&action=reopen`
 *   → `/vendas/553/reabrir?from=notification&action=reopen`
 * - `/sale?situation=Em%20Autorizacao`
 *   → `/vendas?situation=Em%20Autorizacao`
 * - `/audit/sale-reopen?groupId=171`
 *   → `/auditoria/reabertura-vendas?groupId=171`
 * - `/budget`
 *   → `/orcamento`
 * - `/budget/143`
 *   → `/orcamento?budgetId=143`
 *
 * @param {string|null|undefined} deeplink
 * @param {{
 *   notificationType?: string|null,
 *   requesterUserId?: number|null,
 * }} [options] Hints opcionais do VM (persistem na query para sobreviver a refresh).
 * @returns {string|null}
 */
export function toSpaSaleNotificationPath(deeplink, options = {}) {
  if (typeof deeplink !== "string" || !deeplink.trim()) {
    return null;
  }

  try {
    const url = new URL(deeplink.trim(), "http://local.invalid");
    const saleId = extractSaleIdFromPathname(url.pathname);

    if (saleId) {
      return buildSaleReopenSpaPath(url, saleId, options);
    }

    if (isSaleListPathname(url.pathname)) {
      const search = url.searchParams.toString();
      return search ? `/vendas?${search}` : "/vendas";
    }

    if (isAuditSaleReopenPathname(url.pathname)) {
      const search = url.searchParams.toString();
      return search
        ? `/auditoria/reabertura-vendas?${search}`
        : "/auditoria/reabertura-vendas";
    }

    const budgetId = extractBudgetIdFromPathname(url.pathname);
    if (budgetId) {
      return buildBudgetSpaPath(url, budgetId);
    }

    if (isBudgetListPathname(url.pathname)) {
      const search = url.searchParams.toString();
      return search ? `/orcamento?${search}` : "/orcamento";
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * @param {URL} url
 * @param {string} saleId
 * @param {{
 *   notificationType?: string|null,
 *   requesterUserId?: number|null,
 * }} options
 * @returns {string}
 */
function buildSaleReopenSpaPath(url, saleId, options) {
  if (
    typeof options.notificationType === "string" &&
    options.notificationType.trim() &&
    !url.searchParams.get("notificationType") &&
    !url.searchParams.get("type")
  ) {
    url.searchParams.set("notificationType", options.notificationType.trim());
  }

  if (
    options.requesterUserId != null &&
    Number(options.requesterUserId) > 0 &&
    !url.searchParams.get("notifyUserId") &&
    !url.searchParams.get("requesterUserId") &&
    !url.searchParams.get("requestedByUserId")
  ) {
    url.searchParams.set(
      "requesterUserId",
      String(Math.trunc(Number(options.requesterUserId))),
    );
  }

  const search = url.searchParams.toString();
  const qs = search ? `?${search}` : "";
  return `/vendas/${encodeURIComponent(saleId)}/reabrir${qs}`;
}

/**
 * Lista de vendas (sem id na path).
 * @param {string} pathname
 * @returns {boolean}
 */
function isSaleListPathname(pathname) {
  if (typeof pathname !== "string" || !pathname) return false;
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    normalized === "/sale" ||
    normalized === "/api/sale" ||
    normalized === "/vendas"
  );
}

/**
 * Auditoria de reabertura (deeplink API → SPA).
 * @param {string} pathname
 * @returns {boolean}
 */
function isAuditSaleReopenPathname(pathname) {
  if (typeof pathname !== "string" || !pathname) return false;
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    normalized === "/audit/sale-reopen" ||
    normalized === "/api/audit/sale-reopen" ||
    normalized === "/auditoria/reabertura-vendas"
  );
}

/**
 * Lista de orçamentos (sem id na path).
 * @param {string} pathname
 * @returns {boolean}
 */
function isBudgetListPathname(pathname) {
  if (typeof pathname !== "string" || !pathname) return false;
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    normalized === "/budget" ||
    normalized === "/api/budget" ||
    normalized === "/orcamento"
  );
}

/**
 * @param {URL} url
 * @param {string} budgetId
 * @returns {string}
 */
function buildBudgetSpaPath(url, budgetId) {
  if (!url.searchParams.get("budgetId")) {
    url.searchParams.set("budgetId", budgetId);
  }
  const search = url.searchParams.toString();
  return search ? `/orcamento?${search}` : "/orcamento";
}

/**
 * @param {string} pathname
 * @returns {string|null}
 */
function extractBudgetIdFromPathname(pathname) {
  if (typeof pathname !== "string" || !pathname) return null;

  const orcamentoMatch = pathname.match(/\/orcamento\/([^/?#]+)\/?$/i);
  if (orcamentoMatch?.[1]) {
    return decodeURIComponent(orcamentoMatch[1]);
  }

  const budgetMatch = pathname.match(/\/(?:api\/)?budget\/([^/?#]+)\/?$/i);
  if (budgetMatch?.[1]) {
    return decodeURIComponent(budgetMatch[1]);
  }

  return null;
}

/**
 * @param {string} pathname
 * @returns {string|null}
 */
function extractSaleIdFromPathname(pathname) {
  if (typeof pathname !== "string" || !pathname) return null;

  const vendasMatch = pathname.match(/\/vendas\/([^/?#]+)\/reabrir\/?$/i);
  if (vendasMatch?.[1]) {
    return decodeURIComponent(vendasMatch[1]);
  }

  const apiOrSaleMatch = pathname.match(/\/(?:api\/)?sale\/([^/?#]+)/i);
  if (apiOrSaleMatch?.[1]) {
    return decodeURIComponent(apiOrSaleMatch[1]);
  }

  return null;
}
